import React from 'react';
import PropTypes from 'prop-types';
import { Container, Grid } from 'semantic-ui-react';
import { Helmet, flattenHTMLToAppURL } from '@plone/volto/helpers';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import {
  // TypeLabel,
  ViewTitle,
  QuickInfo,
  BookingExperience,
  Services,
  RelatedItems,
  Prices,
  Location,
  ContactsInfos,
  Gallery,
  ButtonLink,
  VideoViewer,
} from '@package/components';

import ViewCTABox from '@package/components/Blocks/CtaBox/View';
// import { ConditionalEmbed } from 'volto-gdpr-privacy';
import InEvidence from './InEvidence';

const messages = defineMessages({
  quando: {
    id: 'Quando',
    defaultMessage: 'Quando',
  },
  download_pdf: {
    id: 'Scarica PDF',
    defaultMessage: 'Scarica PDF',
  },
});

const GallerySettings = {
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        infinite: true,
        dots: true,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 1,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
  adaptiveHeight: true,
};

const ExperienceView = ({ content, location }) => {
  const intl = useIntl();
  return (
    <Container className="experience-view">
      <Helmet title={content.title} />
      <article id="content">
        <Grid
          stackable
          columns={2}
          relaxed="very"
          className="grid-content-wrapper"
        >
          <Grid.Column mobile={16} tablet={16} computer={8}>
            <header>
              {/* <TypeLabel type={content['@type']} /> hanno chiesto di toglierlo*/}
              <ViewTitle
                title={content.title}
                description={content.description}
                url={location?.pathname}
                content={content}
              />
            </header>

            <div className="mobile-only">
              <InEvidence content={content} />
              <BookingExperience content={content} />
              <Prices prices={content.prices} />
            </div>

            <QuickInfo obj={content} />

            <section id="content-core">
              {content.text && (
                <div
                  className="p-no-margin"
                  dangerouslySetInnerHTML={{
                    __html: flattenHTMLToAppURL(content.text.data),
                  }}
                />
              )}
              <Services services={content.services} columns={2} />
            </section>
            {content.call_to_action && (
              <ViewCTABox data={content.call_to_action} />
            )}

            {/* Calendario */}
            {content.calendario_notes && (
              <div
                className="calendario"
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.calendario_notes.data),
                }}
              />
            )}

            <div className="mobile-only">
              <Location content={content} />
            </div>
          </Grid.Column>

          <Grid.Column mobile={16} tablet={16} computer={4}>
            <aside className="right-column">
              <BookingExperience content={content} />
              <InEvidence content={content} />
              <ContactsInfos
                content={content}
                timetable_title={intl.formatMessage(messages.quando)}
              />
              {(content.pdf_url || content.pdf_file?.download) && (
                <section className="download">
                  <ButtonLink
                    to={content.pdf_url || content.pdf_file.download}
                    title={intl.formatMessage(messages.download_pdf)}
                    button={true}
                  />
                </section>
              )}
              <Prices prices={content.prices} />
              <Location content={content} />
            </aside>
          </Grid.Column>
        </Grid>

        {/* Gallery */}
        {content.items?.length > 0 && (
          <section className="images full-width bg-light py-2">
            <Container>
              <Gallery
                items={content.items}
                customSettings={GallerySettings}
                imageScale="large"
                square={true}
              />
            </Container>
          </section>
        )}

        {/* Video */}
        {content.video_link && (
          <section className="full-video">
            <VideoViewer url={content.video_link} align="full" />
          </section>
        )}

        {/* Esperienze correlate */}
        {content.related_experiences?.length > 0 && (
          <FormattedMessage
            id="relatedExperiences"
            defaultMessage="Potrebbe piacerti anche"
          >
            {(message) => (
              <RelatedItems
                items={content.related_experiences}
                title={message}
                className="related-experiences"
              />
            )}
          </FormattedMessage>
        )}

        {/* Schede tematiche */}
        {content.thematic_sheets?.length > 0 && (
          <FormattedMessage
            id="schedeTematicheCorrelate"
            defaultMessage="Per approfondire"
          >
            {(message) => (
              <RelatedItems
                items={content.thematic_sheets}
                title={message}
                className="thematic-sheets"
              />
            )}
          </FormattedMessage>
        )}
      </article>
    </Container>
  );
};
ExperienceView.propTypes = {
  content: PropTypes.shape({
    '@type': PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    text_body: PropTypes.shape({
      data: PropTypes.string,
    }),
    duration: PropTypes.string,
    needs_booking: PropTypes.bool,
    target_audience: PropTypes.shape({
      title: PropTypes.string,
    }),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        url: PropTypes.string,
        image: PropTypes.object,
        image_caption: PropTypes.string,
        '@type': PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default ExperienceView;
